import React from 'react'
import cn from 'classnames'
import { Button } from 'antd'
import { observer } from 'mobx-react'
import { ColumnsType } from 'antd/es/table'
import { TableActions } from './table/Actions'
import { CheckOutlined } from '@ant-design/icons'
import { createExtraField, TExtraField } from '../../../models/ExtraField'
import { ExtraFieldsSettingsModal } from './extraFieldsSettings/Modal'
import styles from './ExtraFieldsSettings.module.scss'
import { t } from '../../../config/i18n'
import { Table } from './Table'

interface Props {
  fields: TExtraField[]
  onChange: (fields: TExtraField[]) => void
  className?: string
}

export const ExtraFieldsSettings = observer(({ fields, className, onChange }: Props) => {
  const [editField, setEditField] = React.useState<TExtraField | null>(null)

  const onEditField = React.useCallback((field: TExtraField) => {
    setEditField({ ...field })
  }, [])

  const onSaveField = React.useCallback((field: TExtraField) => {
    const fieldIdx = fields.findIndex(i => i.uiKey === field.uiKey)
    const nextFields = fieldIdx === -1
      ? [...fields, field]
      : [...fields.slice(0, fieldIdx), field, ...fields.slice(fieldIdx + 1)]
    onChange(nextFields)
    setEditField(null)
  }, [fields, onChange])

  const onDeleteField = React.useCallback((field: TExtraField) => {
    const nextFields = fields.filter(i => i.uiKey !== field.uiKey)
    onChange(nextFields)
  }, [fields, onChange])

  const columns: ColumnsType<TExtraField> = [
    {
      dataIndex: 'name',
      title: t.common.name,
    },
    {
      dataIndex: 'extraId',
      title: 'extraId',
    },
    {
      dataIndex: 'fieldType',
      title: t.catalog.fieldType,
    },
    {
      key: 'required',
      dataIndex: 'required',
      title: t.catalog.fieldReq,
      align: 'center',
      render: (_, item) => item.required && <Check />
    },
    {
      key: 'unique',
      dataIndex: 'unique',
      title: t.catalog.fieldUniq,
      align: 'center',
      render: (_, item) => item.unique && <Check />
    },
    {
      key: 'displayInGrid',
      dataIndex: 'displayInGrid',
      title: t.catalog.inGrid,
      align: 'center',
      render: (_, item) => item.displayInGrid && <Check />
    },
    {
      key: 'actions',
      dataIndex: 'actions',
      title: '',
      width: 50,
      render: (_, item) => (
        <TableActions
          onSettings={() => onEditField(item)}
          onDelete={() => onDeleteField(item)}
        />
      ),
    },
  ]

  return (
    <div className={cn(styles.root, className)}>
      {fields.length > 0 && (
        <Table
          idField={'name'}
          items={fields}
          columns={columns}
        />
      )}

      <Button
        className={styles.add}
        onClick={() => onEditField(createExtraField())}
      >
        {t.catalog.addField}
      </Button>

      {editField && (
        <ExtraFieldsSettingsModal
          field={editField}
          onSave={onSaveField}
          onCancel={() => setEditField(null)}
        />
      )}
    </div>
  )
})

const Check = () => <CheckOutlined style={{ color: 'green' }} />
